// Platform overview data for the operator console home (mockup 40 —
// "Panorama de la plataforma"). The INHERTA team's at-a-glance pulse across ALL
// tenants: system health, clients needing attention, account-manager load, and
// the latest events.
//
// Wired: getPlatformOverview() -> GET /admin/overview, a
// cross-tenant aggregate (operator/staff only). KPIs (clients, conversations +
// messages today, active bots), attention queue (tenants with waiting handoffs
// / past-due), AM load + the event feed are real; component health currently
// defaults to "ok" (live per-component health is a follow-up).

import { apiClient } from '../../api/client.js'

export interface OverviewKpi {
  id: string
  label: string
  value: string
  delta?: { dir: 'up' | 'down' | 'flat'; text: string; note?: string; goodWhen?: 'up' | 'down' }
  sparkline?: number[]
}

export type SystemStatus = 'ok' | 'warn' | 'down'
export interface SystemComponent {
  id: string
  name: string
  detail: string
  status: SystemStatus
  statusLabel: string
}

export type IssueTone = 'sens' | 'warn' | 'info'
export interface AttentionItem {
  id: string
  /** Links to the tenant detail when present. */
  tenantId?: string
  /** Platform rows (ARCO, CFDI queue) link to their surface instead. */
  href?: string
  name: string
  initials: string
  meta: string
  issue: { tone: IssueTone; text: string }
  accountManager: string
}

export type AmStatus = 'active' | 'high' | 'away'
export interface AccountManagerLoad {
  id: string
  name: string
  initials: string
  caption: string
  loadPct: number
  status: AmStatus
  statusLabel: string
}

export type EventKind = 'ok' | 'info' | 'warn'
export interface PlatformEvent {
  id: string
  kind: EventKind
  text: string
  meta: string
}

export interface PlatformOverview {
  kpis: OverviewKpi[]
  systems: SystemComponent[]
  attention: AttentionItem[]
  accountManagers: AccountManagerLoad[]
  events: PlatformEvent[]
}

/**
 * GET /admin/overview — cross-tenant pulse for the operator home: KPIs,
 * attention queue, AM load and the event feed are real; component health
 * still defaults to "ok".
 */
export async function getPlatformOverview(): Promise<PlatformOverview> {
  const res = await apiClient.get<{ data: PlatformOverview }>('/admin/overview')
  return res.data.data
}

// ─── Parked handoffs (operator visibility) ───────────────────────────────────

export interface ParkedHandoff {
  id: string
  tenant: { id: string; name: string }
  state: string
  /** ISO timestamp the conversation entered the handoff state. */
  parkedSince: string
  contact: string | null
  /** "Keep with a human" — excluded from auto-return. */
  sticky: boolean
}

// ── Meta send canary (Phase 4a) ──────────────────────────────────────────────
// The nightly real-Graph-API check. `unknown` is NOT a failure: it means the
// check has never recorded a result, which is a different thing from the channel
// being broken and must not be shown as an outage.

export interface CanaryStatus {
  status: 'ok' | 'failed' | 'unknown'
  checkedAt: string | null
  latencyMs: number | null
  messageId: string | null
  reason: string | null
}

/**
 * GET /admin/canary — latest result of the nightly real Graph-API send check.
 * `unknown` means no result recorded yet, which must not render as an outage.
 */
export async function getCanaryStatus(): Promise<CanaryStatus> {
  const res = await apiClient.get<{ data: CanaryStatus }>('/admin/canary')
  return res.data.data
}

/** Queue a check now. The worker performs the send; poll getCanaryStatus for it. */
export async function runCanaryNow(): Promise<void> {
  await apiClient.post('/admin/canary/run', {})
}
